/**
 * Error codes used throughout the application
 * Synchronized with backend error codes from LogCluster-RITA
 */
export const ErrorCode = {
  // Authentication errors
  MISSING_TOKEN: 'MISSING_TOKEN',
  EMPTY_TOKEN: 'EMPTY_TOKEN',
  INVALID_TOKEN: 'INVALID_TOKEN',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  USER_EXPIRED: 'USER_EXPIRED',
  API_GATEWAY_NOT_ALLOWED: 'API_GATEWAY_NOT_ALLOWED',
  JWT_CONFIG_ERROR: 'JWT_CONFIG_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  USER_ROLE_INVALID: 'USER_ROLE_INVALID',

  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',

  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',

  // Permission errors
  PERMISSION_DENIED: 'PERMISSION_DENIED',

  // Data errors
  RECORD_NOT_FOUND: 'RECORD_NOT_FOUND',
  DUPLICATE_VALUE: 'DUPLICATE_VALUE',
  CONSTRAINT_VIOLATION: 'CONSTRAINT_VIOLATION',

  // General
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

/**
 * Error severity levels for UI presentation
 */
export const ErrorLevel = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical'
} as const;

export type ErrorLevel = (typeof ErrorLevel)[keyof typeof ErrorLevel];

/**
 * Application error structure
 */
export interface AppError {
  /** Error code for programmatic handling */
  code: ErrorCode | string;
  /** Human-readable error message */
  message: string;
  /** Severity level of the error */
  level?: ErrorLevel;
  /** Additional error details for debugging */
  details?: unknown;
  /** Timestamp when error occurred */
  timestamp?: number;
}

/**
 * Backend API error response format
 */
export interface ApiErrorResponse {
  /** Always false for error responses */
  success: false;
  /** Error code from backend */
  code: string;
  /** Error message from backend */
  message: string;
  /** ISO 8601 timestamp */
  timestamp?: string;
}

/**
 * Interface for error handling implementations
 */
export interface ErrorHandler {
  handle(error: AppError): void;
}

/**
 * Toast notification positions
 */
export const ErrorNotificationPosition = {
  TOP_CENTER: 'top-center',
  TOP_RIGHT: 'top-right',
  BOTTOM_CENTER: 'bottom-center',
  BOTTOM_RIGHT: 'bottom-right'
} as const;

export type ErrorNotificationPosition = (typeof ErrorNotificationPosition)[keyof typeof ErrorNotificationPosition];
